import imgAlert3 from '@/assets/imgs/avatar/avatar-3.svg';
import imgAlert4 from '@/assets/imgs/avatar/avatar-4.svg';
import imgAlert5 from '@/assets/imgs/avatar/avatar-5.svg';
import imgAlert6 from '@/assets/imgs/avatar/avatar-6.svg';
import imgAlert7 from '@/assets/imgs/avatar/avatar-7.svg';
import imgAlert10 from '@/assets/imgs/avatar/avatar-10.svg';
import imgAlert11 from '@/assets/imgs/avatar/avatar-11.svg';
import imgAlert13 from '@/assets/imgs/avatar/avatar-13.svg';
import imgAlert15 from '@/assets/imgs/avatar/avatar-15.svg';
import imgAlert16 from '@/assets/imgs/avatar/avatar-16.svg';
import imgAlert17 from '@/assets/imgs/avatar/avatar-17.svg';
import imgAlert18 from '@/assets/imgs/avatar/avatar-18.svg';
import imgAlert19 from '@/assets/imgs/avatar/avatar-19.svg';
import imgAlert20 from '@/assets/imgs/avatar/avatar-20.svg';
import { NotificationAdvice } from '@/interfaces/AnalyticsInterface/AnalyticsInterface';

import { StatusAlertVariant } from './StatusAlert';

// Avisos métricos (ritmo del PDV), calculados localmente. Conservan su
// `priority` histórica (1-7) porque está ligada a la analítica.
export enum StatusAlertId {
  PDV_INACTIVE_2M = 'pdvInactive2m',
  PDV_INACTIVE_1M = 'pdvInactive1m',
  PENDING_APPROVED = 'pendingApproved',
  LOW_PACE_PREV_MONTH = 'lowPacePrevMonth',
  IS_NEAR_PACE_PREV_MONTH = 'isNearPacePrevMonth',
  GOOD_PACE = 'goodPace',
  PENDING_CAPTURE = 'pendingCapture',
}

// Avisos dirigidos por evento (resultado de una operación en Pepper One).
// Tienen su propia `priority` (1-6) y, prevalecen SIEMPRE
// sobre cualquier aviso métrico (ver `useAlertHandlersActions`).
export enum EventPepperOneAlertId {
  OPERATION_ACTIVATED = 'operationActivated',
  OPERATION_DENIED = 'operationDenied',
  OPERATION_FIRST = 'operationFirst',
  OPERATION_COUNTER_OFFER = 'operationCounterOffer',
  OPERATION_HIGH_AMOUNT = 'operationHighAmount',
  OPERATION_COUNTER_OFFER_REJECTED = 'operationCounterOfferRejected',
}

export enum StatusAlertActionId {
  NEW_OPERATION = 'newOperation',
  FILTER_APPROVED = 'filterApproved',
  FILTER_CAPTURE = 'filterCapture',
  // Lleva a los datos de contacto comercial (página /contact).
  CONTACT = 'contact',
}

// Contrato del postMessage que emite Pepper One (iframe) al terminar la
// operación; Comercios (host) lo escucha para disparar el aviso por evento.
export const OPERATION_OUTCOME_MESSAGE = 'Pepper - Banner Outcome';

export enum OperationOutcome {
  ACTIVATED = 'activated',
  DENIED = 'denied',
  COUNTER_OFFER = 'counterOffer',
  COUNTER_OFFER_REJECTED = 'counterOfferRejected',
  HIGH_AMOUNT = 'belenderOrHighAmount',
}

export interface OperationOutcomeMessage {
  message: typeof OPERATION_OUTCOME_MESSAGE;
  outcome: OperationOutcome;
  amount?: number;
  proposalId?: string;
}

export interface StatusAlertCopy {
  title: string;
  message: string;
  image?: string;
}

// Forma común a ambas alertas.
export interface StatusAlertBaseConfig {
  priority: number;
  variant: StatusAlertVariant;
  web: StatusAlertCopy;
  app: StatusAlertCopy;
  ctaLabel: string;
  actionId: StatusAlertActionId;
  // Avisos por evento: par de avatares [día impar, día par]. Cuando está
  // presente prevalece sobre `copy.image` al resolver el aviso.
  avatars?: readonly [string, string];
}

export interface StatusAlertConfig extends StatusAlertBaseConfig {
  id: StatusAlertId;
}

export interface EventPepperOneAlertConfig extends StatusAlertBaseConfig {
  id: EventPepperOneAlertId;
}

// Unión de alertas
export type AlertId = StatusAlertId | EventPepperOneAlertId;
export type AnyAlertConfig = StatusAlertConfig | EventPepperOneAlertConfig;

export const ALERT_ANALYTICS_META: Partial<
Record<AlertId, { message: number; advice: NotificationAdvice }>
> = {
  [StatusAlertId.PDV_INACTIVE_2M]: {
    message: 1,
    advice: NotificationAdvice.INACTIVE_2M,
  },
  [StatusAlertId.PDV_INACTIVE_1M]: {
    message: 2,
    advice: NotificationAdvice.INACTIVE_1M,
  },
  [StatusAlertId.LOW_PACE_PREV_MONTH]: {
    message: 3,
    advice: NotificationAdvice.LOW_VS_PREV_MONTH,
  },
  [StatusAlertId.IS_NEAR_PACE_PREV_MONTH]: {
    message: 4,
    advice: NotificationAdvice.LOW_VS_BEST_6M,
  },
  [StatusAlertId.GOOD_PACE]: {
    message: 6,
    advice: NotificationAdvice.GOOD_VS_PREV_MONTH,
  },
  [StatusAlertId.PENDING_APPROVED]: {
    message: 7,
    advice: NotificationAdvice.PENDING_APPROVED,
  },
  [StatusAlertId.PENDING_CAPTURE]: {
    message: 8,
    advice: NotificationAdvice.PENDING_CAPTURE,
  },
  [EventPepperOneAlertId.OPERATION_ACTIVATED]: {
    message: 9,
    advice: NotificationAdvice.ONE_REACTIVATION,
  },
  [EventPepperOneAlertId.OPERATION_DENIED]: {
    message: 10,
    advice: NotificationAdvice.ONE_DENIED,
  },
  [EventPepperOneAlertId.OPERATION_FIRST]: {
    message: 11,
    advice: NotificationAdvice.ONE_FIRST,
  },
  [EventPepperOneAlertId.OPERATION_COUNTER_OFFER]: {
    message: 12,
    advice: NotificationAdvice.ONE_COUNTER_OFFER,
  },
  [EventPepperOneAlertId.OPERATION_HIGH_AMOUNT]: {
    message: 13,
    advice: NotificationAdvice.ONE_HIGH_AMOUNT,
  },
  [EventPepperOneAlertId.OPERATION_COUNTER_OFFER_REJECTED]: {
    message: 14,
    advice: NotificationAdvice.ONE_REJECTED_COUNTER_OFFER,
  },
};
// avisos por evento (Pepper One). Prioridad propia 1-6
// muestran avatar según paridad de día.
export const EVENT_PEPPER_ONE_ALERTS: Record<
EventPepperOneAlertId,
EventPepperOneAlertConfig
> = {
  [EventPepperOneAlertId.OPERATION_ACTIVATED]: {
    id: EventPepperOneAlertId.OPERATION_ACTIVATED,
    priority: 2,
    variant: StatusAlertVariant.SUCCESS,
    web: {
      title: 'Enhorabuena! Acabas de cerrar una venta financiada con Pepper.',
      message:
        'Mantén la dinámica ofreciendo siempre Pepper y recuerda que puedes financiar express hasta 10.000€',
    },
    app: {
      title: '¡Venta abonada!',
      message:
        'Sigue sumando con Pepper hasta 10.000€',
    },
    ctaLabel: 'Seguir financiando',
    actionId: StatusAlertActionId.NEW_OPERATION,
    avatars: [imgAlert6, imgAlert7],
  },
  [EventPepperOneAlertId.OPERATION_DENIED]: {
    id: EventPepperOneAlertId.OPERATION_DENIED,
    priority: 3,
    variant: StatusAlertVariant.ALERT,
    web: {
      title: 'Lo sentimos, no hemos podido aprobar tu financiación.',
      message: 'Contacta con nosotros para saber más o buscar una alternativa',
    },
    app: {
      title: 'No hemos podido aprobarla.',
      message: 'Llámanos y buscamos una alternativa',
    },
    ctaLabel: 'Datos de contacto',
    actionId: StatusAlertActionId.CONTACT,
    avatars: [imgAlert17, imgAlert18],
  },
  [EventPepperOneAlertId.OPERATION_FIRST]: {
    id: EventPepperOneAlertId.OPERATION_FIRST,
    priority: 1,
    variant: StatusAlertVariant.SUCCESS,
    web: {
      title:
        'Enhorabuena! Acabamos de abonar tu primera venta financiada con Pepper.',
      message:
        'Esperamos que te haya encantado y sea la primera de muchas. Recuerda que puedes financiar express hasta 10.000€ y que estamos siempre para ti',
    },
    app: {
      title: '¡Primera venta abonada!',
      message:
        'Sigue creciendo con Pepper hasta 10.000€',
    },
    ctaLabel: 'Seguir financiando',
    actionId: StatusAlertActionId.NEW_OPERATION,
    avatars: [imgAlert6, imgAlert7],
  },
  [EventPepperOneAlertId.OPERATION_COUNTER_OFFER]: {
    id: EventPepperOneAlertId.OPERATION_COUNTER_OFFER,
    priority: 4,
    variant: StatusAlertVariant.SUCCESS,
    web: {
      title: 'Enhorabuena! Gracias a Siempre Sí has cerrado una venta.',
      message:
        'Financia siempre con Pepper para tener disponible este producto y no perder ninguna venta',
    },
    app: {
      title: '¡Venta cerrada con Siempre Sí!',
      message:
        'Más opciones para no perder ventas',
    },
    ctaLabel: 'Seguir financiando',
    actionId: StatusAlertActionId.NEW_OPERATION,
    avatars: [imgAlert15, imgAlert16],
  },
  [EventPepperOneAlertId.OPERATION_HIGH_AMOUNT]: {
    id: EventPepperOneAlertId.OPERATION_HIGH_AMOUNT,
    priority: 5,
    variant: StatusAlertVariant.SUCCESS,
    web: {
      title:
        'Enhorabuena! Acabas de financiar {amount} en 2 minutos y sin documentación!',
      message:
        'Financia siempre con Pepper para tener disponible este producto y cerrar tus ventas al instante',
    },
    app: {
      title:
        '¡Financiación express!',
      message:
        'Has financiado {amount} en 2 minutos y sin documentación.',
    },
    ctaLabel: 'Seguir financiando',
    actionId: StatusAlertActionId.NEW_OPERATION,
    avatars: [imgAlert15, imgAlert16],
  },
  [EventPepperOneAlertId.OPERATION_COUNTER_OFFER_REJECTED]: {
    id: EventPepperOneAlertId.OPERATION_COUNTER_OFFER_REJECTED,
    priority: 6,
    variant: StatusAlertVariant.WARNING,
    web: {
      title:
        '¡Ya casi lo tienes!',
      message: 'Sólo falta aceptar la alternativa para confirmar la financiación y cerrar la venta.',
    },
    app: {
      title:
        '¡Ya casi lo tienes!',
      message: 'Acepta la alternativa para confirmar y cerrar la venta.',
    },
    ctaLabel: 'Datos de contacto',
    actionId: StatusAlertActionId.CONTACT,
    avatars: [imgAlert19, imgAlert20],
  },
};

// avisos métricos. Prioridad histórica 1-7
export const STATUS_ALERTS: Record<StatusAlertId, StatusAlertConfig> = {
  [StatusAlertId.PDV_INACTIVE_2M]: {
    id: StatusAlertId.PDV_INACTIVE_2M,
    priority: 1,
    variant: StatusAlertVariant.ALERT,
    web: {
      image: imgAlert11,
      title: 'Te echamos de menos.',
      message:
        'Aprovecha las campañas que tienes disponibles para financiaciones express hasta 10.000€ y no pierdas ninguna venta',
    },
    app: {
      title: 'Empieza a sumar este mes.',
      message:
        'Aprovecha las campañas express hasta 10.000€ para abonar tu primera operación',
    },
    ctaLabel: 'Crear operación',
    actionId: StatusAlertActionId.NEW_OPERATION,
  },
  [StatusAlertId.PDV_INACTIVE_1M]: {
    id: StatusAlertId.PDV_INACTIVE_1M,
    priority: 2,
    variant: StatusAlertVariant.ALERT,
    web: {
      image: imgAlert10,
      title: 'Este mes aún no tienes operaciones abonadas.',
      message:
        'Aprovecha las campañas que tienes disponibles para financiaciones express hasta 10.000€ y no pierdas ninguna venta',
    },
    app: {
      title: 'Empieza a sumar este mes.',
      message:
        'Aprovecha las campañas express hasta 10.000€ para abonar tu primera operación',
    },
    ctaLabel: 'Crear operación',
    actionId: StatusAlertActionId.NEW_OPERATION,
  },
  [StatusAlertId.PENDING_APPROVED]: {
    id: StatusAlertId.PENDING_APPROVED,
    priority: 3,
    variant: StatusAlertVariant.WARNING,
    web: {
      image: imgAlert13,
      title: 'Finaliza tus financiaciones aprobadas.',
      message:
        'Tienes ventas aprobadas pendientes de firma o pago, retómalas fácilmente ahora',
    },
    app: {
      title: 'Finaliza tus financiaciones aprobadas.',
      message: 'Tienes ventas aprobadas pendientes de firma o pago.',
    },
    ctaLabel: 'Ver aprobadas',
    actionId: StatusAlertActionId.FILTER_APPROVED,
  },
  [StatusAlertId.LOW_PACE_PREV_MONTH]: {
    id: StatusAlertId.LOW_PACE_PREV_MONTH,
    priority: 4,
    variant: StatusAlertVariant.ALERT,
    web: {
      image: imgAlert3,
      title: 'Tus financiaciones van este mes por debajo de su ritmo habitual.',
      message:
        'Aún estás a tiempo de recuperar tu mejor cifra. Aprovecha las campañas que hemos preparado para ti y ofrece siempre Pepper para cerrar todas tus ventas',
    },
    app: {
      title: 'Recupera ritmo este mes.',
      message: 'Ofrece siempre Pepper para cerrar todas tus ventas',
    },
    ctaLabel: 'Crear operación',
    actionId: StatusAlertActionId.NEW_OPERATION,
  },
  [StatusAlertId.GOOD_PACE]: {
    id: StatusAlertId.GOOD_PACE,
    priority: 5,
    variant: StatusAlertVariant.SUCCESS,
    web: {
      image: imgAlert6,
      title: 'Batir récord este mes.',
      message:
        'Este mes te estás superando. Mantén la dinámica ofreciendo Pepper en tus próximas ventas.',
    },
    app: {
      title: 'Estás batiendo récords.',
      message: 'Sigue sumando operaciones abonadas.',
    },
    ctaLabel: 'Seguir financiando',
    actionId: StatusAlertActionId.NEW_OPERATION,
  },
  [StatusAlertId.IS_NEAR_PACE_PREV_MONTH]: {
    id: StatusAlertId.IS_NEAR_PACE_PREV_MONTH,
    priority: 6,
    variant: StatusAlertVariant.WARNING,
    web: {
      image: imgAlert4,
      title: 'Estás cerca de alcanzar tu ritmo habitual.',
      message:
        'Aún estás a tiempo de recuperar tu mejor cifra. Aprovecha las campañas express hasta 10.000€ y ofrece siempre Pepper para cerrar tus ventas',
    },
    app: {
      title: 'Recupera ritmo este mes.',
      message:
        'Ofrece siempre Pepper express hasta 10.000€ para cerrar todas tus ventas',
    },
    ctaLabel: 'Crear operación',
    actionId: StatusAlertActionId.NEW_OPERATION,
  },
  [StatusAlertId.PENDING_CAPTURE]: {
    id: StatusAlertId.PENDING_CAPTURE,
    priority: 7,
    variant: StatusAlertVariant.WARNING,
    web: {
      image: imgAlert5,
      title: 'Tienes solicitudes pendientes.',
      message:
        'Finalízalas para convertirlas en operaciones abonadas antes de que caduquen.',
    },
    app: {
      title: 'Solicitudes pendientes.',
      message: 'Finalízalas antes de que caduquen.',
    },
    ctaLabel: 'Ver capturas',
    actionId: StatusAlertActionId.FILTER_CAPTURE,
  },
};

export const getStatusAlertConfig = (id: StatusAlertId): StatusAlertConfig => STATUS_ALERTS[id];

export const getEventPepperOneAlertConfig = (
  id: EventPepperOneAlertId,
): EventPepperOneAlertConfig => EVENT_PEPPER_ONE_ALERTS[id];

// Resuelve la config de cualquier aviso (evento o métrico) a partir de su id.
export const getAlertConfig = (id: AlertId): AnyAlertConfig => (id in EVENT_PEPPER_ONE_ALERTS
  ? EVENT_PEPPER_ONE_ALERTS[id as EventPepperOneAlertId]
  : STATUS_ALERTS[id as StatusAlertId]);

// Selecciona el avatar según la paridad del día del mes: el primero en días
// impares, el segundo en días pares.
export const getDayParityAvatar = (
  avatars: readonly [string, string],
  dayOfMonth: number,
): string => (dayOfMonth % 2 === 1 ? avatars[0] : avatars[1]);
